import { AssetGroup, useGrouping } from '#base/AssetGroup.js';
import { useCancel } from '#base/Canvas/Cancel.js';
import { ColorPicker } from '#base/Color.js';
import { DesignOption } from '#base/Designer/index.js';
import {
  GroupMonogramToolbar,
  GroupNameTitleToolbar,
  GroupNameToolbar,
} from '#base/Designer/Toolbars/index.js';
import { ToolKeys, ToolViewModes } from '#base/Designer/types.js';
import { ComboInput, CsvUpload } from '#base/Text/Components.js';
import Button from '#base/ui/Button.js';
import { DEFAULT_COLOR } from '@c2s-npm/plumlib';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

export const GroupToolView = ({
  assets: allAssets,
  groups,
  onSelect,
  selected,
  cancel,
  revert,
  elements,
  hasPremium,
  graphicType,
  defaultColor,
  renderInput: RenderInput,
  renderAssets: RenderAssets,
  placeholder,
  debounce = 300,
  toolKey,
  handleUpload,
  CsvUploadRenderer,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [selectedAsset, setAsset] = useState(
    allAssets.find((f) => f.key === selected?.get?.('assetId')) || null
  );
  const [selectedColor, setColor] = useState(selected?.get?.('color') || defaultColor || DEFAULT_COLOR);
  const [currentAsset, setCurrentAsset] = useState(selectedAsset);
  const [currentColor, setCurrentColor] = useState(selectedAsset);
  const [input, setInput] = useState({});
  const [mode, setMode] = useState(ToolViewModes.INPUT);

  const [commit, undo, canRevert, isNew] = useCancel(selected, cancel, revert);

  const [subGroups, assets, selectedGroup, selectGroup, hasPremiumElement, hasOtherElements] = useGrouping({
    groups,
    assets: allAssets,
    autoSelectGroup: selectedAsset?.key,
    elements,
    assetGroupFilter: [],
  });
  const showPremiumWarning = (hasPremium === 'item' || hasOtherElements) && hasPremiumElement;

  useEffect(() => {
    if (!selected && assets.length > 0) {
      setAsset(assets[0]);
    }
  }, [selected, assets]);

  useEffect(() => {
    setCurrentAsset(selectedAsset);
  }, [selectedAsset]);

  useEffect(() => {
    setCurrentColor(selectedColor);
  }, [selectedColor]);

  useEffect(() => {
    // TODO make this snappier for color/asset changes
    const timeoutId = setTimeout(() => {
      currentAsset && onSelect(currentAsset, Object.assign({}, input, { color: currentColor }));
    }, debounce);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentAsset, currentColor, input, debounce]);

  const handleNumber = (n) => {
    const data = Array.apply(null, Array(n)).map(() => ({ data: '', isDefault: true }));
    setInput(Object.assign({}, input, { data }));
    setMode(ToolViewModes.INPUT);
  };

  const onCancelClick = () => {
    if (mode === ToolViewModes.INPUT) undo();
    else {
      onSelect(selectedAsset, input);
      setCurrentAsset(selectedAsset);
      setCurrentColor(selectedColor);
      setMode(ToolViewModes.INPUT);
    }
  };

  const onAssetConfirmUpdate = () => {
    setAsset(currentAsset);
    setColor(currentColor);
    setMode(ToolViewModes.INPUT);
  };

  const getUploadLabelName = (toolKey) => {
    switch (toolKey) {
      case ToolKeys.GROUP_JERSEY:
        return 'Add Names & Numbers';

      case ToolKeys.GROUP_NAME_TITLE:
        return 'Add Names & Titles';

      case ToolKeys.GROUP_MONOGRAM:
        return 'Add Initials';

      case ToolKeys.GROUP_NAME:
        return 'Add Names';

      default:
        return 'Add Name';
    }
  };

  return (
    <>
      <div className='t-mx-auto t-max-w-[360px]'>
        <div className='t-mb-4 t-block t-rounded-t-xl lg:t-relative lg:-t-left-10 lg:-t-top-8 lg:t-z-10 lg:t-mx-auto lg:t-mb-0 lg:t-w-[calc(100%+80px)] lg:t-bg-white lg:t-px-10 lg:t-py-3 lg:t-shadow-[0px_6px_6px_#00000019]'>
          {toolKey === ToolKeys.GROUP_NAME && (
            <GroupNameToolbar {...{ selected, mode, setMode, graphicType }} />
          )}
          {toolKey === ToolKeys.GROUP_MONOGRAM && (
            <GroupMonogramToolbar {...{ selectedAsset: currentAsset, mode, setMode, graphicType }} />
          )}
          {toolKey === ToolKeys.GROUP_JERSEY && <GroupMonogramToolbar {...{ mode, setMode, graphicType }} />}
          {toolKey === ToolKeys.GROUP_NAME_TITLE && (
            <GroupNameTitleToolbar {...{ mode, setMode, graphicType }} />
          )}
        </div>

        {mode === ToolViewModes.INPUT && (
          <RenderInput
            {...{
              selected,
              input,
              setInput,
              commit,
              undo,
              canRevert,
              isNew,
              placeholder,
              graphicType,
            }}
          />
        )}
      </div>

      <div
        className={classNames({
          't-hidden': mode !== ToolViewModes.ASSET && mode !== ToolViewModes.ASSET_FRAMES,
          't-block': mode === ToolViewModes.ASSET || mode === ToolViewModes.ASSET_FRAMES,
        })}>
        <AssetGroup groups={subGroups} {...{ selectedGroup, selectGroup }} />
        {subGroups.length === 0 && assets.length === 0 && (
          <em className='t-font-bold t-not-italic t-text-muted'>Empty group</em>
        )}
        {assets.length > 0 && (
          <RenderAssets
            {...{
              selected,
              input,
              setInput,
              assets,
              selectedAsset: currentAsset,
              setAsset: setCurrentAsset,
              hasPremium: hasOtherElements || hasPremium === 'item',
              placeholder,
              graphicType,
              defaultColor,
              mode: toolKey ? mode : undefined,
            }}
          />
        )}

        {showPremiumWarning && (
          <div className='t-mt-4 t-text-left t-text-brand-red'>
            Premium assets cannot be used alongside other designs and text.
          </div>
        )}
      </div>

      <div
        className={classNames({
          't-hidden': mode !== ToolViewModes.COLOR,
          't-block': mode === ToolViewModes.COLOR,
        })}>
        <ColorPicker value={currentColor} onSelect={setCurrentColor} />
      </div>

      {mode === 'inputMethod' && (
        <div>
          <DesignOption text='Upload a list' onClick={() => setMode('upload')} />
          <DesignOption text='Enter Manually' onClick={() => setMode('number')} />
        </div>
      )}
      {mode === 'upload' && (
        <div>
          <CsvUpload
            filename='Template.csv'
            uploadLabelName={getUploadLabelName(toolKey)}
            fields={[{ label: 'Name', value: 'data' }]}
            onChange={handleUpload}
            renderer={CsvUploadRenderer}
          />
        </div>
      )}
      {mode === 'number' && (
        <div>
          <p>How many people are you looking to personalize for?</p>
          <ComboInput onChange={handleNumber} />
        </div>
      )}

      {mode !== ToolViewModes.INPUT && (
        <div className='t-mt-3 t-flex t-items-center t-justify-center t-gap-1 t-text-center md:t-mt-6 lg:t-flex-col'>
          <Button
            variant='primary'
            size={isMobile ? 'sm' : 'base'}
            fullWidth='all'
            onClick={onAssetConfirmUpdate}>
            {getButtonLabel(mode, toolKey)}
          </Button>

          <Button variant='link' size={isMobile ? 'sm' : 'base'} onClick={onCancelClick}>
            {mode === ToolViewModes.INPUT ? 'Cancel' : 'Back'}
          </Button>
        </div>
      )}
    </>
  );
};

const getButtonLabel = (mode, toolKey) => {
  if (mode === ToolViewModes.COLOR) return 'Update Color';

  if (toolKey === ToolKeys.GROUP_NAME) {
    if (mode === ToolViewModes.ASSET) return 'Update Font';
  }

  if ([ToolKeys.MONOGRAM, ToolKeys.JERSEY].includes(toolKey)) {
    if (mode === ToolViewModes.ASSET) return 'Update Style';
    if (mode === ToolViewModes.ASSET_FRAMES) return 'Update Frame'; // applies on monogram only
  }

  return 'Update';
};
